import React, { useState, useEffect, useRef } from 'react';
import { Tv, Wifi } from 'lucide-react';
import { customerAPI } from '../../services/api';

// Debounced customer suggestions dropdown, shared by the web sidebar and app/tablet topbars.
// Capped at SUGGEST_LIMIT (still just a preview, not the full result set) — when a search
// term matches more than that, a "View all" row below hands off to the real Customers list
// page, which is fully paginated and not capped, instead of trying to cram everything here.
// Uses onMouseDown + imperative navigate (via onViewAll), same trick as the row onPick below —
// a real <Link>'s click-based navigation would get canceled by the input's onBlur closing this
// dropdown (and unmounting the Link) in the gap between mousedown and click.
const SUGGEST_LIMIT = 20;

export default function SearchSuggestions({ query, onPick, onViewAll }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    clearTimeout(debounce.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    debounce.current = setTimeout(async () => {
      try {
        const results = await customerAPI.search(q, SUGGEST_LIMIT);
        setResults(results);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [query]);

  if (query.trim().length < 2) return null;

  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
      background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 8,
      boxShadow: '0 10px 30px rgba(0,0,0,.15)', zIndex: 200, maxHeight: 360, overflowY: 'auto',
    }}>
      {loading && <div style={{ padding: '10px 14px', fontSize: '.82rem', color: 'var(--text-muted)' }}>Searching…</div>}
      {!loading && results.length === 0 && (
        <div style={{ padding: '10px 14px', fontSize: '.82rem', color: 'var(--text-muted)' }}>No customers found</div>
      )}
      {!loading && results.map(c => {
        const Icon = c.customerType === 'catv' ? Tv : Wifi;
        return (
          <div
            key={c._id}
            onMouseDown={() => onPick(c)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Icon size={14} color={c.customerType === 'catv' ? 'var(--primary)' : 'var(--accent)'} style={{ flexShrink: 0 }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '.85rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                {c.customerId} · {c.mobile}{c.broadband?.username ? ` · ${c.broadband.username}` : ''}{c.catv?.accountNumber ? ` · A/C ${c.catv.accountNumber}` : ''}
              </div>
              {(() => {
                const society = c.address?.area || '';
                const houseNo = c.broadband?.houseNumber || c.catv?.houseNumber || '';
                if (!society && !houseNo) return null;
                return (
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {society}{houseNo ? ` - ${houseNo}` : ''}
                  </div>
                );
              })()}
            </div>
            <div style={{ flexShrink: 0, fontSize: '.75rem', fontWeight: 700, color: (c.outstandingBalance || 0) > 0 ? 'var(--c-dc2626)' : 'var(--c-16a34a)' }}>
              ₹{(c.outstandingBalance || 0).toLocaleString('en-IN')}
            </div>
          </div>
        );
      })}
      {!loading && results.length >= SUGGEST_LIMIT && (
        <div
          onMouseDown={() => onViewAll(query.trim())}
          style={{
            textAlign: 'center', padding: '9px 14px', cursor: 'pointer',
            fontSize: '.8rem', fontWeight: 600, color: 'var(--primary)',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          View all results for "{query.trim()}" →
        </div>
      )}
    </div>
  );
}
